import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import DepotInjectionForm from "../DepotInjectionChart/DepotInjectionForm";
import {
    fetchDepotInjectionChart,
    updateDepotInjectionChart,
} from "../../../store/slices/depotInjectionChartsSlice";

const EditDepotInjection = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { isLoading, error, currentDepotInjectionChart } = useSelector(
        (state) => state.depotInjectionCharts
    );

    const [formData, setFormData] = useState({
        name: "",
        mrNo: "",
        dob: "",
        roomNo: "",
        documentName: "",
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: "",
        injectionRecords: [],
    });

    const [errors, setErrors] = useState({});

    // ✅ Fetch the record on mount (like in NursingRecord)
    useEffect(() => {
        dispatch(fetchDepotInjectionChart(id))
            .unwrap()
            .then((response) => {
                const data = response.data; // your API returns { message, data }

                setFormData({
                    patientId: patientId || data.patientId,
                    caseId: caseId || data.caseId,
                    name: data.name || "",
                    mrNo: data.mr_no || "",
                    dob: data.dob || "",
                    roomNo: data.room_no || "",
                    documentName: data.document_name || "",
                    effectiveDate: data.effective_date || "",
                    approvedBy: data.approved_by || "",
                    approvedDate: data.approved_date || "",
                    nextRevisionBy: data.next_revision_by || "",
                    nextRevisionDate: data.next_revision_date || "",
                    injectionRecords:
                        data.injection_records?.map((r) => ({
                            id: r.id,
                            dateTime: r.date_time,
                            injectionName: r.injection_name,
                            mg: r.mg,
                            route: r.route,
                            duration: r.duration,
                            dueDate: r.due_date,
                        })) || [],
                });
            })
            .catch((err) => {
                Swal.fire({
                    title: "Error",
                    text: err?.message || "Failed to fetch Depot Injection Chart.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            });
    }, [dispatch, id]);

    // ✅ Submit handler (same pattern as NursingRecord)
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || formData.injectionRecords.length === 0) {
            setErrors({
                name: !formData.name ? "Name is required." : "",
                injectionRecords:
                    formData.injectionRecords.length === 0
                        ? "At least one injection record is required."
                        : "",
            });
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before updating.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            name: formData.name,
            mr_no: formData.mrNo,
            dob: formData.dob,
            room_no: formData.roomNo,
            document_name: formData.documentName,
            effective_date: formData.effectiveDate,
            approved_by: formData.approvedBy,
            approved_date: formData.approvedDate,
            next_revision_by: formData.nextRevisionBy,
            next_revision_date: formData.nextRevisionDate,
            injection_records: formData.injectionRecords.map((r) => ({
                id: r.id,
                date_time: r.dateTime,
                injection_name: r.injectionName,
                mg: r.mg,
                route: r.route,
                duration: r.duration,
                due_date: r.dueDate,
            })),
        };

        try {
            await dispatch(updateDepotInjectionChart({ case_id: caseId, chartData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Depot Injection Chart updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err?.message || "Failed to update Depot Injection Chart.",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    if (isLoading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/depot-injections/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        {error && (
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {error}
                            </div>
                        )}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter(Boolean)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <DepotInjectionForm
                                formData={formData}
                                setFormData={setFormData}
                                handleSubmit={handleSubmit}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditDepotInjection;
